'use client';

import React, {
  CSSProperties,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { createPortal } from 'react-dom';
import { DotSpinner } from 'components/shared';
import { useCartStore, useShallow } from 'components/shared/Cart';
import { CrossBVModal } from 'assets/icons/CrossBVModal';
import { CheckCircleSF, InfoIcon } from 'assets/icons';
import { AddedToCartDisclaimer } from 'components/shared/Cart/UpsellModal/UpsellCardUIComponents';
import { Currency } from 'utils/Currency';
import { useFormatPrice, getCurrencySymbolFromEnum } from 'hooks/pricing/useFormattedPrice';
import { ShopifyStore } from 'lib/commerce/shopify/shopifyHelper';
import { ProductType } from 'components/shared/Cart/types';
import { useTranslations } from 'next-intl';
import {
  BvUpsellCard,
  getUsBvVariantSetForVariantId,
} from 'content/products/bloodVisionUpsell';
import {
  getAeCitySuggestionPopoverLayout,
  getUsBvMergedDisplay,
  getUsBvSharedCardSnapshot,
  isBvCardInCart,
} from 'utils/bloodVisionUpsell';
import {
  computeBvDisplayPricePersistentSheet,
  formatBvAtcPriceLabel,
  getBvCanAddToCartEffective,
  getBvDisableAddToCart,
  getBvHasResolvedBackendPriceCombined,
  getBvIsAddButtonActive,
  getBvShopifyVariantIdForAtc,
  shouldUseBvUsSharedSnapshot,
} from 'utils/bvUpsellPresentation';
import { useBvPincodeValidator } from 'hooks/blood-vision/useBvPincodeValidator';
import { useBvUsZipPlansSnapshot } from 'hooks/blood-vision/useBvUsZipPlansSnapshot';
import { BvUpsellPlanImage } from 'components/blood-vision/BvUpsellPlanImage';
import { useWindowSize } from 'hooks/dom/useWindowSize';
import styles from './BloodVisionUpsellModal.module.css';

const CONSTRAINED_PADDING = 'max(24px, calc((100% - 1016px) / 2 + 24px))';

interface BloodVisionUpsellModalProps {
  card: BvUpsellCard;
  onClose: () => void;
  isCartEmbedded?: boolean;
  isUpsellEmbedded?: boolean;
  onAddToCart?: (shopifyVariantId: string) => Promise<void>;
}

const BloodVisionUpsellModal: React.FC<BloodVisionUpsellModalProps> = ({
  card,
  onClose,
  isCartEmbedded = false,
  isUpsellEmbedded = false,
  onAddToCart,
}) => {
  const t = useTranslations('common');
  const tCart = useTranslations('cart');
  const cartClient = useCartStore(
    useShallow((s) => ({
      store: s.store,
      currency: s.currency,
      cart: s.cart,
      addToCartSilently: s.addToCartSilently,
      showCart: s.showCart,
    })),
  );
  const { width } = useWindowSize();
  const isMobile = width <= 768;
  const store = cartClient.store as ShopifyStore | null;
  const currency = (cartClient.currency ?? Currency.usd) as Currency;
  const currencySymbol = getCurrencySymbolFromEnum(currency);

  const [isAdding, setIsAdding] = useState(false);
  const [showUsPricingInfo, setShowUsPricingInfo] = useState(false);
  const [aeCityPopoverStyle, setAeCityPopoverStyle] =
    useState<CSSProperties | null>(null);
  const [aeCityPopoverPlacement, setAeCityPopoverPlacement] = useState<
    'above' | 'below'
  >('below');
  const aeCityListRef = useRef<HTMLUListElement | null>(null);

  const zipPlans = useBvUsZipPlansSnapshot();
  const sessionSnapForCart = useMemo(
    () =>
      getUsBvSharedCardSnapshot(
        card,
        zipPlans.committedZip,
        zipPlans.shopifyPlansResponse,
      ),
    [
      card,
      zipPlans.committedZip,
      zipPlans.revision,
      zipPlans.shopifyPlansResponse,
    ],
  );
  const variantIdForCartBootstrap = useMemo(
    () =>
      getBvShopifyVariantIdForAtc(store, card, undefined, sessionSnapForCart),
    [store, card, sessionSnapForCart?.resolvedShopifyVariantId],
  );
  const isInCartBootstrap = useMemo(
    () =>
      isBvCardInCart(card, cartClient.cart, store, variantIdForCartBootstrap),
    [card, cartClient.cart, store, variantIdForCartBootstrap],
  );

  const {
    pincode,
    onPincodeChange,
    inputRef,
    inputConfig,
    statusText,
    statusKind,
    canAddToCart,
    isCheckingPincode,
    resolvedPrice,
    hasResolvedBackendPrice,
    resolvedUsPlanDisplay,
    resolvedShopifyVariantId,
    setStatusMessage,
    usBvSharedCardSnapshot: sharedSnap,
    usCommittedZip,
    isAeCityMode,
    aeCitySuggestions,
    showAeCitySuggestions,
    onAeCitySuggestionSelect,
    onAeCityInputFocus,
    closeAeCitySuggestions,
  } = useBvPincodeValidator({
    card,
    store,
    active: !isInCartBootstrap,
    autoFocusInput: !isCartEmbedded && !isUpsellEmbedded,
  });

  const useSharedSnapState =
    !!sharedSnap &&
    shouldUseBvUsSharedSnapshot(store, pincode, usCommittedZip, {
      sheetOpen: true,
    });
  const effectiveUsState = useSharedSnapState
    ? {
        resolvedUsPlanDisplay: sharedSnap.resolvedUsPlanDisplay,
        hasResolvedBackendPrice: false,
        resolvedPrice: undefined as number | undefined,
        canAddToCart: !!sharedSnap.isServiceable,
        resolvedShopifyVariantId: sharedSnap.resolvedShopifyVariantId,
      }
    : {
        resolvedUsPlanDisplay,
        hasResolvedBackendPrice,
        resolvedPrice,
        canAddToCart,
        resolvedShopifyVariantId,
      };

  const displayFields = useMemo(
    () => getUsBvMergedDisplay(store, card, effectiveUsState.resolvedUsPlanDisplay),
    [store, card, effectiveUsState.resolvedUsPlanDisplay],
  );

  const displayPrice = useMemo(
    () =>
      computeBvDisplayPricePersistentSheet(
        effectiveUsState.hasResolvedBackendPrice,
        effectiveUsState.resolvedPrice,
        sharedSnap,
        card.fallbackPrice,
      ),
    [
      effectiveUsState.hasResolvedBackendPrice,
      effectiveUsState.resolvedPrice,
      sharedSnap?.isServiceable,
      sharedSnap?.resolvedPrice,
      card.fallbackPrice,
    ],
  );
  const formatPriceWithComma = useFormatPrice();
  const displayPriceLabel = displayPrice
    ? `${currencySymbol}${formatPriceWithComma(displayPrice)}`
    : null;
  const isUSStore = store === ShopifyStore.US;
  const hasResolvedBackendPriceCombined =
    getBvHasResolvedBackendPriceCombined(store, effectiveUsState.hasResolvedBackendPrice, sharedSnap, {
      kind: 'modal',
    });
  const atcPriceLabel = formatBvAtcPriceLabel(
    isUSStore,
    hasResolvedBackendPriceCombined,
    displayPriceLabel,
  );
  const canAddToCartEffective = getBvCanAddToCartEffective(
    effectiveUsState.canAddToCart,
  );

  const shopifyVariantIdForAtc = useMemo(
    () =>
      getBvShopifyVariantIdForAtc(
        store,
        card,
        effectiveUsState.resolvedShopifyVariantId,
        sharedSnap ?? sessionSnapForCart,
      ),
    [
      store,
      card,
      effectiveUsState.resolvedShopifyVariantId,
      sharedSnap?.resolvedShopifyVariantId,
      sessionSnapForCart?.resolvedShopifyVariantId,
    ],
  );
  const isInCart = useMemo(
    () => isBvCardInCart(card, cartClient.cart, store, shopifyVariantIdForAtc),
    [card, cartClient.cart, store, shopifyVariantIdForAtc],
  );
  const disableAddToCart = getBvDisableAddToCart({
    store,
    isBlockedBecauseInCart: isInCart,
    canAddToCartEffective,
    isCheckingPincode,
    isAdding,
  });
  const isAddButtonActive = getBvIsAddButtonActive({
    store,
    isBlockedBecauseInCart: isInCart,
    canAddToCartEffective,
    isAdding,
  });
  const usExclusiveRegion = useMemo(
    () =>
      store === ShopifyStore.US && shopifyVariantIdForAtc
        ? getUsBvVariantSetForVariantId(shopifyVariantIdForAtc)
        : null,
    [store, shopifyVariantIdForAtc],
  );

  const previouslyFocusedRef = useRef<HTMLElement | null>(null);
  const containerRef = useRef<HTMLDivElement>(null);
  useEffect(() => {
    previouslyFocusedRef.current = document.activeElement as HTMLElement | null;
    return () => {
      previouslyFocusedRef.current?.focus?.();
    };
  }, []);

  useEffect(() => {
    if (isInCart || !isAeCityMode || !showAeCitySuggestions || !inputRef.current) {
      setAeCityPopoverStyle(null);
      return;
    }

    const updatePosition = () => {
      const rect = inputRef.current?.getBoundingClientRect();
      if (!rect) return;
      const { placement, style } = getAeCitySuggestionPopoverLayout(
        rect,
        aeCitySuggestions.length,
      );
      setAeCityPopoverPlacement(placement);
      setAeCityPopoverStyle(style);
    };

    updatePosition();
    window.addEventListener('resize', updatePosition);
    window.addEventListener('scroll', updatePosition, true);
    return () => {
      window.removeEventListener('resize', updatePosition);
      window.removeEventListener('scroll', updatePosition, true);
    };
  }, [isInCart, isAeCityMode, showAeCitySuggestions, inputRef, aeCitySuggestions.length]);

  useEffect(() => {
    if (isInCart || !isAeCityMode || !showAeCitySuggestions) return;
    const onPointerDown = (event: MouseEvent) => {
      const target = event.target as Node | null;
      if (!target) return;
      if (inputRef.current?.contains(target)) return;
      if (aeCityListRef.current?.contains(target)) return;
      closeAeCitySuggestions();
    };
    document.addEventListener('mousedown', onPointerDown);
    return () => document.removeEventListener('mousedown', onPointerDown);
  }, [isInCart, isAeCityMode, showAeCitySuggestions, closeAeCitySuggestions, inputRef]);

  const handleAddToCart = async () => {
    if (isInCart) return;
    if (!canAddToCartEffective) return;
    if (!shopifyVariantIdForAtc) {
      setStatusMessage(
        tCart('sharedCart.upsell.notAvailableForCheckout'),
        'error',
      );
      return;
    }
    setIsAdding(true);
    try {
      if (onAddToCart) {
        await onAddToCart(shopifyVariantIdForAtc);
      } else {
        await cartClient.addToCartSilently([
          {
            shopifyVariantId: shopifyVariantIdForAtc,
            productType: ProductType.GENERIC,
            params: {
              productType: 'BLOOD_VISION',
              maxQuantity: 1,
              noteAttributes: [
                { key: '_funnel', value: 'combined_order' },
                ...(usExclusiveRegion
                  ? [{ key: '_exc_region', value: usExclusiveRegion }]
                  : []),
              ],
            },
          },
        ]);
        setStatusMessage(tCart('sharedCart.upsell.addedToCartStatus'), 'success');
        if (isUpsellEmbedded) {
          onClose();
        } else {
          cartClient.showCart();
          onClose();
        }
      }
    } catch (error) {
      console.error('BV add to cart failed from learn more modal', error);
      setStatusMessage(t('bloodVisionUpsellModal.status.unableToAdd'), 'error');
    } finally {
      setIsAdding(false);
    }
  };

  const isSheetEmbedded = isCartEmbedded || isUpsellEmbedded;

  const titleId = `bv-modal-title-${card.id}`;
  const descriptionId = `bv-modal-description-${card.id}`;
  const pricingNoteId = `bv-modal-pricing-note-${card.id}`;
  const aeCityListboxId = `bv-modal-ae-cities-${card.id}`;

  // Conditional paddings/dimensions resolved to inline CSS vars so the module
  // CSS can set them once at base + once in media without cross-specificity traps.
  const modalContainerStyle = {
    '--mc-radius': isSheetEmbedded ? '24px 24px 0 0' : '24px',
    '--mc-max-width': isSheetEmbedded ? 'none' : '640px',
    '--mc-height': isSheetEmbedded ? 'auto' : '100%',
    '--mc-flex': isSheetEmbedded ? '1' : '0 1 auto',
    '--mc-overflow': isSheetEmbedded ? 'visible' : 'hidden',
    '--mc-height-m': isSheetEmbedded ? 'auto' : '100dvh',
  } as React.CSSProperties;

  const headerSectionStyle = {
    '--hs-pt': isUpsellEmbedded ? '20px' : '32px',
    '--hs-pb': isUpsellEmbedded ? '20px' : '32px',
    '--hs-pl': isSheetEmbedded ? CONSTRAINED_PADDING : '32px',
    '--hs-pr': isSheetEmbedded ? CONSTRAINED_PADDING : '32px',
    '--hs-pt-m': isUpsellEmbedded ? '20px' : '24px',
    '--hs-pb-m': isUpsellEmbedded ? '14px' : '16px',
    '--hs-pl-m': isSheetEmbedded ? CONSTRAINED_PADDING : '24px',
    '--hs-pr-m': isSheetEmbedded ? CONSTRAINED_PADDING : '24px',
  } as React.CSSProperties;

  const usPricingStyle = {
    '--up-pr': isSheetEmbedded ? CONSTRAINED_PADDING : '32px',
    '--up-pb': isUpsellEmbedded ? '20px' : '24px',
    '--up-pl': isSheetEmbedded ? CONSTRAINED_PADDING : '32px',
    '--up-pl-m': isSheetEmbedded ? CONSTRAINED_PADDING : '24px',
    '--up-pr-m': isSheetEmbedded ? CONSTRAINED_PADDING : '24px',
  } as React.CSSProperties;

  const descriptionSectionStyle = {
    '--ds-pt': isUpsellEmbedded ? '20px' : '24px',
    '--ds-pb': isUpsellEmbedded ? '20px' : '24px',
    '--ds-pl': isSheetEmbedded ? CONSTRAINED_PADDING : '32px',
    '--ds-pr': isSheetEmbedded ? CONSTRAINED_PADDING : '32px',
    '--ds-pl-m': isSheetEmbedded ? CONSTRAINED_PADDING : '24px',
    '--ds-pr-m': isSheetEmbedded ? CONSTRAINED_PADDING : '24px',
  } as React.CSSProperties;

  const stickyFooterStyle = {
    '--sf-pr': isSheetEmbedded ? CONSTRAINED_PADDING : '24px',
    '--sf-pl': isSheetEmbedded ? CONSTRAINED_PADDING : '24px',
    '--sf-radius': isSheetEmbedded ? '0' : '24px',
  } as React.CSSProperties;

  return (
    <div
      ref={containerRef}
      className={styles.modalContainer}
      style={modalContainerStyle}
      role="dialog"
      aria-modal="true"
      aria-labelledby={titleId}
      aria-describedby={descriptionId}
    >
      {!isCartEmbedded && !isUpsellEmbedded ? (
        <button
          className={styles.closeButton}
          onClick={onClose}
          aria-label={t(
            'bloodVisionUpsellModal.button.ariaLabel.closeBloodVision',
          )}
        >
          <CrossBVModal />
        </button>
      ) : null}

      <div className={styles.headerSection} style={headerSectionStyle}>
        <div className={styles.thumbnailContainer}>
          <BvUpsellPlanImage
            src={(() => {
              const base = isUpsellEmbedded
                ? card.surfaceImageSrc.crossSellModal
                : card.surfaceImageSrc.productListingCart;
              return isMobile ? base.replace('/desktop/', '/mobile/') : base;
            })()}
            alt={displayFields.title}
            width={64}
            height={64}
            style={{ objectFit: 'contain' }}
          />
        </div>
        <div className={styles.headerInfo}>
          <h2
            className={styles.productName}
            id={titleId}
            data-cart-embedded={isCartEmbedded ? 'true' : undefined}
          >
            {displayFields.title}
          </h2>
          <span className={styles.headerPrice}>
            {displayPriceLabel ?? tCart('sharedCart.upsell.priceAtCheckout')}
          </span>
        </div>
      </div>

      {store === ShopifyStore.US ? (
        <p className={styles.usPricingDisclaimer} style={usPricingStyle}>
          {t('bloodVisionModal.text.bloodVisionPricing')}
        </p>
      ) : null}

      <div className={styles.divider} />

      <div
        className={styles.modalContent}
        data-cart-embedded={isCartEmbedded ? 'true' : undefined}
        data-sheet-embedded={isSheetEmbedded ? 'true' : undefined}
      >
        <div className={styles.descriptionSection} style={descriptionSectionStyle}>
          <h3 className={styles.staticSectionTitle}>
            {t('bloodVisionModal.subheading.about')}
          </h3>
          <p className={styles.descriptionText}>{displayFields.learnMoreAbout}</p>
        </div>

        <div className={styles.divider} />

        <div className={styles.descriptionSection} style={descriptionSectionStyle}>
          <h3 className={styles.staticSectionTitle}>
            {t('bloodVisionUpsellModal.subheading.keyBiomarkersTested')}
          </h3>
          <ul className={styles.detailsList}>
            {card.learnMoreContent.keyBiomarkers.map((item) => (
              <li className={styles.detailItem} key={item.title}>
                <div>
                  <span className={styles.detailItemTitle}>{item.title}</span>
                  {item.description ? (
                    <>
                      {' - '}
                      <span className={styles.detailItemDescription}>
                        {item.description}
                      </span>
                    </>
                  ) : null}
                </div>
              </li>
            ))}
          </ul>
        </div>
      </div>

      <div className={styles.stickyFooter} style={stickyFooterStyle}>
        {isInCart ? (
          <div className={styles.addedToCartFooterRow}>
            <AddedToCartDisclaimer className={styles.modalAddedToCartDisclaimer}>
              <CheckCircleSF />
              {tCart('sharedCart.upsell.addedToCart')}
            </AddedToCartDisclaimer>
          </div>
        ) : (
          <>
            <div className={styles.innerTitleRow}>
              <div className={styles.innerTitle}>
                {inputConfig.checkSectionTitle}
              </div>
              {store === ShopifyStore.US ? (
                <button
                  className={styles.pricingInfoTrigger}
                  type="button"
                  onClick={() => setShowUsPricingInfo((value) => !value)}
                  aria-label={
                    showUsPricingInfo
                      ? t('bloodVisionModal.button.ariaLabel.hideZipPricing')
                      : t('bloodVisionModal.button.ariaLabel.showZipPricing')
                  }
                  aria-expanded={showUsPricingInfo}
                  aria-controls={pricingNoteId}
                >
                  <InfoIcon fill="#000000" />
                </button>
              ) : null}
            </div>
            {store === ShopifyStore.US && showUsPricingInfo ? (
              <p
                className={styles.inlinePricingNote}
                id={pricingNoteId}
                role="note"
                aria-label={t(
                  'bloodVisionModal.text.ariaLabel.usPricingInformation',
                )}
              >
                <strong>
                  {t('bloodVisionModal.emphasis.bloodVisionPricing')}
                </strong>{' '}
                {t('bloodVisionModal.text.certainStatesSuch')}
              </p>
            ) : null}

            <div className={styles.pincodeInputShell}>
              <input
                className={styles.pincodeInput}
                ref={inputRef}
                value={pincode}
                onChange={(event) => onPincodeChange(event.target.value)}
                onFocus={() => {
                  if (isAeCityMode) onAeCityInputFocus();
                }}
                inputMode={isAeCityMode ? 'text' : 'numeric'}
                maxLength={inputConfig.inputMaxLength}
                placeholder={inputConfig.inputPlaceholder}
                role={isAeCityMode ? 'combobox' : undefined}
                aria-autocomplete={isAeCityMode ? 'list' : undefined}
                aria-expanded={
                  isAeCityMode ? Boolean(showAeCitySuggestions) : undefined
                }
                aria-controls={isAeCityMode ? aeCityListboxId : undefined}
                aria-label={tCart('sharedCart.upsell.enterField', {
                  label: inputConfig.inputLabel,
                })}
                style={
                  isAeCityMode && showAeCitySuggestions
                    ? {
                        ...(aeCityPopoverPlacement === 'above'
                          ? {
                              borderTopLeftRadius: 0,
                              borderTopRightRadius: 0,
                            }
                          : {
                              borderBottomLeftRadius: 0,
                              borderBottomRightRadius: 0,
                            }),
                      }
                    : undefined
                }
              />
              <span className={styles.inputLoader} aria-hidden>
                {isCheckingPincode ? <DotSpinner color="#057ff0" /> : null}
              </span>
            </div>
            <p
              className={`${styles.statusText}${statusKind ? ` ${statusKind}` : ''}`}
              role="status"
              aria-live="polite"
            >
              {statusText}
            </p>
            <button
              className={`${styles.finalAddToCartButton} gtm${
                isAddButtonActive ? ' active' : ''
              }`}
              onClick={handleAddToCart}
              disabled={disableAddToCart}
              aria-busy={isAdding}
              data-buttontype={`upsell-modal-bv-${card.id}`}
              data-variantid={shopifyVariantIdForAtc ?? ''}
            >
              {isAdding ? <DotSpinner color="#FFFFFF" /> : atcPriceLabel}
            </button>
          </>
        )}
      </div>
      {!isInCart &&
      isAeCityMode &&
      showAeCitySuggestions &&
      aeCityPopoverStyle &&
      typeof document !== 'undefined'
        ? createPortal(
            <ul
              className={styles.aeCityList}
              id={aeCityListboxId}
              ref={aeCityListRef}
              role="listbox"
              aria-label={tCart(
                'sharedCart.upsell.availableCities',
              )}
              style={{
                ...aeCityPopoverStyle,
                borderRadius:
                  aeCityPopoverPlacement === 'above'
                    ? '10px 10px 0 0'
                    : '0 0 10px 10px',
              }}
            >
              {aeCitySuggestions.map((city) => (
                <li className={styles.aeCityOption} key={city.id}>
                  <button
                    type="button"
                    onMouseDown={(event) => event.preventDefault()}
                    onClick={() => onAeCitySuggestionSelect(city.name)}
                  >
                    {city.name}
                  </button>
                </li>
              ))}
            </ul>,
            document.body,
          )
        : null}
    </div>
  );
};

export default BloodVisionUpsellModal;
